import React, { useState, useEffect, useContext } from 'react';
import { groupBy } from '../utils/displayHelper';
import types from '../types/services-api';
import agent from '../agent';
import { PropsWithChildren } from 'react';

type InterviewContextType = {
  errorMessage: string;
  interviews: types.UserInterviewViewType[] | undefined;
  interviewsByDate: { [key: string]: types.UserInterviewViewType[] };
  positionOptions: string[] | null;
  updateInterviews: (updatedInterview: types.UserInterviewViewType) => void;
  addInterview: (newInterview: types.UserInterviewViewType) => void;
};

export const InterviewContext = React.createContext<InterviewContextType>({
  errorMessage: '',
  interviews: undefined,
  interviewsByDate: {},
  positionOptions: null,
  updateInterviews: () => {},
  addInterview: () => {},
});

const InterviewContextWrapper = (props: PropsWithChildren) => {
  const [interviews, setInterviews] = useState<types.UserInterviewViewType[]>();
  const [interviewsByDate, setInterviewsByDate] = useState<{
    [key: string]: types.UserInterviewViewType[];
  }>({});
  const [positionOptions, setPositionOptions] = useState<string[] | null>(null);

  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchInterviews();
  }, []);

  function fetchInterviews() {
    agent.User.getInterviews({ date: 'all' })
      .then((res) => {
        setInterviews(res.data);
        const groupedInterviews = groupBy(res.data, 'interviewDate');
        setInterviewsByDate(groupedInterviews);
        const updatedPositions = Array.from(
          new Set(
            res.data.map(
              (interview: types.UserInterviewViewType) => interview.position
            )
          )
        );
        setPositionOptions(updatedPositions);
      })
      .catch((err) => {
        setErrorMessage(err.message);
      });
  }

  function updateInterviews(updatedInterview: types.UserInterviewViewType) {
    const updatedInterviews = !!interviews
      ? interviews.map((interview) => {
          if (interview.username === updatedInterview.username) {
            return updatedInterview;
          } else {
            return interview;
          }
        })
      : [updatedInterview];
    const updatedInterviewsByDate = groupBy(updatedInterviews, 'interviewDate');
    setInterviews(updatedInterviews);
    setInterviewsByDate(updatedInterviewsByDate);
  }

  function addInterview(newInterview: types.UserInterviewViewType) {
    const updatedInterviews = !!interviews
      ? [...interviews, newInterview]
      : [newInterview];
    const updatedInterviewsByDate = groupBy(updatedInterviews, 'interviewDate');
    setInterviews(updatedInterviews);
    setInterviewsByDate(updatedInterviewsByDate);
  }

  InterviewContext.displayName = 'Interview Context';

  return (
    <InterviewContext.Provider
      value={{
        errorMessage,
        interviews,
        interviewsByDate,
        positionOptions,
        updateInterviews,
        addInterview,
      }}
    >
      {props.children}
    </InterviewContext.Provider>
  );
};

export default InterviewContextWrapper;
